import React from 'react';
import PropTypes from 'prop-types';
import { useCommunicationChannelAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import MetricSection from '../dashboard/core/MetricSection';
import BarChart from '../ui/BarChart';
import { formatPercent } from '../../utils/format';
import { FILTER_PERIODS } from '../../constants';
import './ChannelAnalytics.css';

/**
 * Channel Analytics Component
 * Measures effectiveness of email, push notifications, and in-app messaging
 */
const ChannelAnalytics = ({ period = FILTER_PERIODS.ALL }) => {
  const { data, loading, error } = useCommunicationChannelAnalytics(period);

  if (error) {
    return (
      <div className="channel-analytics error">
        <h3>Error loading communication channel data</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="channel-analytics metric-loading" aria-live="polite" aria-busy="true">
        <div className="loading-spinner" />
        <p>Loading communication channel data...</p>
      </div>
    );
  }

  if (!data || !data.channels) {
    return (
      <div className="channel-analytics no-data-message">
        <i className="bi bi-info-circle icon-brand"></i>
        <p>No communication channel data available for this period.</p>
      </div>
    );
  }

  const { channels, summary } = data;

  // Email metrics
  const emailMetrics = [
    {
      id: 'email-sent',
      title: 'Emails Sent',
      value: channels.email.totalSent,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'email-failed',
      title: 'Emails Failed',
      value: channels.email.totalFailed,
      formatType: 'number',
      color: 'red'
    },
    {
      id: 'email-success-rate',
      title: 'Success Rate',
      value: channels.email.successRate,
      formatType: 'number',
      subtitle: '%',
      color: 'green'
    },
    {
      id: 'email-conversion',
      title: 'Conversion Rate',
      value: channels.email.conversionRate,
      formatType: 'number',
      subtitle: '%',
      color: 'teal'
    }
  ];

  // Push notification metrics
  const pushMetrics = [
    {
      id: 'push-sent',
      title: 'Push Sent',
      value: channels.pushNotifications.totalSent,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'push-delivered',
      title: 'Push Delivered',
      value: channels.pushNotifications.totalDelivered,
      formatType: 'number',
      color: 'green'
    },
    {
      id: 'push-failed',
      title: 'Push Failed',
      value: channels.pushNotifications.totalFailed,
      formatType: 'number',
      color: 'red'
    },
    {
      id: 'push-delivery-rate',
      title: 'Delivery Rate',
      value: channels.pushNotifications.deliveryRate,
      formatType: 'number',
      subtitle: '%',
      color: 'teal'
    }
  ];

  // Overall metrics
  const overallMetrics = [
    {
      id: 'total-sent',
      title: 'Total Sent',
      value: summary.totalSent,
      formatType: 'number',
      color: 'blue'
    },
    {
      id: 'total-delivered',
      title: 'Total Delivered',
      value: summary.totalDelivered,
      formatType: 'number',
      color: 'green'
    },
    {
      id: 'overall-delivery-rate',
      title: 'Overall Delivery Rate',
      value: summary.overallDeliveryRate,
      formatType: 'number',
      subtitle: '%',
      color: 'teal'
    }
  ];

  // Channel comparison chart
  const channelComparisonData = [
    { label: 'Email', value: channels.email.totalSent },
    { label: 'Push Notifications', value: channels.pushNotifications.totalSent },
    { label: 'In-App', value: channels.inApp.totalSent }
  ];

  return (
    <div className="channel-analytics">
      <div className="channel-summary">
        <MetricCard
          title="Overall Delivery Rate"
          value={summary.overallDeliveryRate}
          formatType="number"
          subtitle="%"
          color="green"
          loading={loading}
        />
      </div>

      <div className="channel-grid">
        <MetricSection
          title="Email Analytics"
          icon="bi-envelope"
          description="Email delivery and conversion metrics"
          metrics={emailMetrics}
          loading={loading}
        />

        <MetricSection
          title="Push Notification Analytics"
          icon="bi-bell"
          description="Push notification delivery metrics"
          metrics={pushMetrics}
          loading={loading}
        />

        <MetricSection
          title="Overall Performance"
          icon="bi-graph-up"
          description="Combined communication channel metrics"
          metrics={overallMetrics}
          loading={loading}
        />
      </div>

      <div className="channel-comparison">
        <h3>Channel Comparison</h3>
        <div className="chart-container">
          <BarChart
            data={channelComparisonData}
            label="Messages Sent"
            formatType="number"
            color="accent"
            orientation="vertical"
          />
        </div>
      </div>
    </div>
  );
};

ChannelAnalytics.propTypes = {
  period: PropTypes.string.isRequired
};

export default ChannelAnalytics;

